import { useTranslation } from 'react-i18next';
import type { Profile } from '@/auth/OperatorProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';

type EligibleContext = Profile['eligibleContexts'][number];

/**
 * Read-only rendering of FR-SP-3: the caller's own current active context (echoed by
 * `GET /v1/profile` only when still one of the caller's own eligible pairs, SL-0022 Resolution 5)
 * and the full set of (Tenant, Organization) pairs the caller is eligible to select. Purely
 * presentational (Resolution 5) — no control here selects or changes the active context; that
 * remains the shell's own `ContextSelector` (SL-0015).
 */
export function ContextsSection({
  activeContext,
  eligibleContexts,
}: {
  activeContext: Profile['activeContext'];
  eligibleContexts: readonly EligibleContext[];
}) {
  const { t } = useTranslation(['profile', 'common']);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="card-label">{t('contexts.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm">
          {activeContext === null ? (
            t('contexts.noActiveContext')
          ) : (
            <>
              {t('contexts.activeContextPrefix')} <code>{activeContext.tenantId}</code>,{' '}
              {t('contexts.andOrganization')} <code>{activeContext.organizationId}</code>
            </>
          )}
        </p>
        <DataTable
          columns={[
            { accessorKey: 'code', header: t('contexts.codeHeader') },
            { accessorKey: 'name', header: t('contexts.nameHeader') },
            {
              accessorKey: 'tenantId',
              header: t('contexts.tenantIdHeader'),
              cell: ({ getValue }) => <code>{getValue<string>()}</code>,
            },
            {
              id: 'current',
              header: t('contexts.currentHeader'),
              cell: ({ row }: { row: { original: EligibleContext } }) =>
                activeContext !== null && row.original.id === activeContext.organizationId
                  ? t('common:yes')
                  : '',
            },
          ]}
          data={[...eligibleContexts]}
          emptyMessage={t('contexts.noEligibleContext')}
        />
      </CardContent>
    </Card>
  );
}
